import { Badge, Box, Button, Divider, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { Bell, FileText, HardDriveDownload, KeyRound, Receipt } from "lucide-react";
import { useEffect, useId, useMemo, useState, type JSX, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../auth/AuthProvider.js";
import { getOverview, keys, listLicences } from "../../../lib/crmApi.js";
import { listXopQuotes, xopQuoteKeys } from "../../../lib/xopQuotesApi.js";
import { listReleases } from "../releases/api.js";
import { RELEASES_QUERY_KEY } from "../releases/useReleases.js";
import {
  KIND_LABEL,
  buildNotifications,
  loadSeen,
  saveSeen,
  unseenCount,
  type NotificationItem,
  type NotificationKind,
} from "./notifications.js";

/**
 * The bell in the portal's header: what has changed since this person last
 * looked. Overdue invoices, quotes waiting for a decision, licences expiring
 * or expired, versions XOP has shipped — one list, newest first, every row
 * a link to the thing itself.
 *
 * It reads the SAME queries the pages run (the overview, the quotes list,
 * the licences list, the releases list), under the same query keys, so it
 * adds no round trips on a page that already fetched them and never
 * disagrees with the page it links to. Feeds behind a permission the person
 * lacks are simply not asked for.
 *
 * "Seen" is per person, in the browser (`notifications.ts`): opening the
 * bell marks every current row seen, and the badge counts the rows that
 * were not. Nothing about this is server state — the facts are already the
 * server's; only "have I looked" is the browser's.
 */
const KIND_ICON: Record<NotificationKind, typeof Bell> = {
  invoice: Receipt,
  quote: FileText,
  licence: KeyRound,
  release: HardDriveDownload,
};

export function NotificationBell(): JSX.Element {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const email = user?.email ?? "";
  const [seen, setSeen] = useState<Set<string>>(() => loadSeen(email));
  useEffect(() => setSeen(loadSeen(email)), [email]);

  const seesInvoices = can("invoices:view");
  const seesLicences = can("licences:view");
  const seesReleases = can("documentation:view");

  const overview = useQuery({ queryKey: keys.overview, queryFn: getOverview });
  const quotes = useQuery({
    queryKey: xopQuoteKeys.list("all"),
    queryFn: () => listXopQuotes("all"),
    enabled: seesInvoices,
  });
  const licences = useQuery({
    queryKey: keys.licences(),
    queryFn: () => listLicences(),
    enabled: seesLicences,
  });
  const releases = useQuery({
    queryKey: RELEASES_QUERY_KEY,
    queryFn: listReleases,
    enabled: seesReleases,
  });

  const items = useMemo(
    () =>
      buildNotifications({
        overview: seesInvoices ? overview.data : null,
        quotes: quotes.data?.quotes,
        licences: licences.data?.licences,
        releases: releases.data,
      }),
    [seesInvoices, overview.data, quotes.data, licences.data, releases.data],
  );
  const unseen = unseenCount(items, seen);

  const open = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const close = (): void => {
    setAnchorEl(null);
    // Opening the bell IS reading it: everything listed is now seen.
    setSeen(saveSeen(email, items));
  };
  const go = (item: NotificationItem): void => {
    close();
    navigate(item.to);
  };

  const grouped = useMemo(() => {
    const out = new Map<NotificationKind, NotificationItem[]>();
    for (const item of items) {
      const list = out.get(item.kind);
      if (list) list.push(item);
      else out.set(item.kind, [item]);
    }
    return out;
  }, [items]);

  return (
    <>
      <IconButton
        aria-label={unseen > 0 ? `Notifications, ${unseen} new` : "Notifications"}
        aria-controls={anchorEl ? menuId : undefined}
        aria-haspopup="menu"
        aria-expanded={anchorEl ? "true" : undefined}
        onClick={open}
        data-testid="notification-bell"
        sx={{ color: "text.secondary", flexShrink: 0 }}
      >
        <Badge
          badgeContent={unseen}
          color="error"
          max={99}
          invisible={unseen === 0}
          data-testid="notification-badge"
        >
          <Bell size={20} />
        </Badge>
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-label": "Notifications", dense: true }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: "calc(100vw - 16px)", maxHeight: 520 } } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 0.75, display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>Notifications</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }} data-testid="notification-summary">
            {items.length === 0
              ? "Nothing needs you"
              : unseen > 0
                ? `${unseen} new · ${items.length} total`
                : `${items.length} total`}
          </Typography>
        </Box>
        <Divider />
        {items.length === 0 ? (
          <Typography sx={{ px: 2, py: 2.5, fontSize: 13, color: "text.secondary" }}>
            No overdue invoices, no quotes waiting, no licences lapsing, and nothing new from XOP
            Networks since you last looked.
          </Typography>
        ) : (
          [...grouped.entries()].map(([kind, rows]) => {
            const Icon = KIND_ICON[kind];
            return (
              <Box key={kind} data-testid={`notifications-${kind}`}>
                <Typography
                  sx={{
                    px: 2,
                    pt: 1.25,
                    pb: 0.5,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "text.disabled",
                  }}
                >
                  {KIND_LABEL[kind]}
                </Typography>
                {rows.map((item) => {
                  const isNew = !seen.has(item.key);
                  return (
                    <MenuItem
                      key={item.key}
                      onClick={() => go(item)}
                      data-testid={`notification-${item.key}`}
                      sx={{
                        alignItems: "flex-start",
                        gap: 1.25,
                        py: 1,
                        whiteSpace: "normal",
                        bgcolor: (t) => (isNew ? alpha(t.palette.primary.main, 0.05) : "transparent"),
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          mt: "2px",
                          flexShrink: 0,
                          color:
                            item.severity === "error"
                              ? "error.main"
                              : item.severity === "warning"
                                ? "warning.main"
                                : "primary.main",
                        }}
                      >
                        <Icon size={16} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: isNew ? 600 : 500, lineHeight: 1.3 }}>
                          {item.title}
                          {isNew && (
                            <Box
                              component="span"
                              sx={{
                                ml: 0.75,
                                px: 0.6,
                                borderRadius: 1,
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                color: "primary.main",
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                              }}
                            >
                              New
                            </Box>
                          )}
                        </Typography>
                        {item.detail && (
                          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.35 }}>
                            {item.detail}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Box>
            );
          })
        )}
        {items.length > 0 && (
          <>
            <Divider sx={{ mt: 0.5 }} />
            <Box sx={{ px: 1, py: 0.75, display: "flex", justifyContent: "flex-end" }}>
              <Button size="small" onClick={close}>
                Mark all as read
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
}
